import { Component, inject, type OnDestroy, type OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogConfig,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Subscription } from 'rxjs';
import type { ThreadOrTaskMode } from '../../../server/types/message-types';
import type { Org, OrgSpace, OrgUser } from '../../../server/types/org-types';
import type { DeepReadonly } from '../../../server/util-type';
import { AuthUserService } from '../auth-user.service';
import { MessageService, ThreadItem } from '../message.service';
import { TaskService } from '../task.service';
import {
  ThreadLeaveDialogComponent,
  type ThreadLeaveDialogComponentData,
} from '../thread-leave-dialog/thread-leave-dialog.component';

export type ThreadEditUsersData = {
  authUserId: string;
  orgId: string;
  refId: string;
  mode: ThreadOrTaskMode;
};

@Component({
  selector: 'app-thread-edit-users-dialog',
  templateUrl: './thread-edit-users-dialog.component.html',
  styleUrls: ['./thread-edit-users-dialog.component.scss'],
  standalone: false,
})
export class ThreadEditUsersDialogComponent implements OnInit, OnDestroy {
  private dialogRef = inject(MatDialogRef<ThreadEditUsersDialogComponent>);
  private readonly data: ThreadEditUsersData = inject(MAT_DIALOG_DATA);
  private authUserService = inject(AuthUserService);
  private snackBar = inject(MatSnackBar);
  private messageService = inject(MessageService);
  private taskService = inject(TaskService);
  private dialog = inject(MatDialog);

  authUserId: string;
  orgId: string;
  refId: string;
  mode: ThreadOrTaskMode;

  users: OrgUser[];
  assignedTo?: string;

  threadItem: ThreadItem;

  org: DeepReadonly<Org>;
  space: DeepReadonly<OrgSpace>;

  viewOnly = false;

  selectUserControl: FormControl<string>;
  filteredUsers: OrgUser[] = [];
  existingFilteredUsers: OrgUser[] = [];
  notInSpaceFilteredUsers: OrgUser[] = [];

  pageState: 'waiting' | 'ready' | 'error' = 'waiting';

  subscriptions = new Subscription();

  constructor() {
    this.authUserId = this.data.authUserId;
    this.orgId = this.data.orgId;
    this.refId = this.data.refId;
    this.mode = this.data.mode;
  }

  ngOnInit(): void {
    const { orgId, refId, mode } = this;
    this.threadItem = this.messageService.getThreadItem({ orgId, refId, mode });

    const userSub = this.threadItem.userIdsReplayObservable.subscribe(
      (userIds) => {
        const users: OrgUser[] = [];
        userIds.forEach((userId) => {
          const user = this.authUserService.getUser({ orgId, userId });
          if (user) users.push(user);
        });
        this.users = users;
      }
    );
    this.subscriptions.add(userSub);

    const handleTask = (task) => (this.assignedTo = task.assignedTo);
    const getTaskSub = this.taskService
      .getTaskAsync({ orgId, taskId: refId })
      .subscribe(handleTask);
    this.subscriptions.add(getTaskSub);
    const taskUpdateSub = this.taskService
      .taskUpdates({ taskId: refId })
      .subscribe(handleTask);
    this.subscriptions.add(taskUpdateSub);

    this.selectUserControl = new FormControl('', { nonNullable: true });
    const searchSub = this.selectUserControl.valueChanges.subscribe((value) => {
      this.updateFilteredUsers(value);
    });
    this.subscriptions.add(searchSub);

    const sub = this.authUserService.userOrgObj.subscribe((userOrgObj) => {
      if (!userOrgObj) return; // This should not happen except at logout

      this.org = userOrgObj.org;

      if (this.mode === 'task') {
        const spaceId = this.threadItem.task?.spaceId;
        if (!spaceId) {
          console.error('spaceId could not be determined');
          this.pageState = 'error';
          return;
        }
        const space = this.org.spaces.find((s) => s._id === spaceId);
        if (!space) {
          console.error('space not found');
          this.pageState = 'error';
          return;
        }
        this.space = space;
      }

      // Initialize search control
      if (!this.selectUserControl.value) this.updateFilteredUsers('');

      this.pageState = 'ready';
    });
    this.subscriptions.add(sub);

    const userIdsSubscribe = this.threadItem.userIdsReplayObservable.subscribe(
      (userIds) => {
        this.viewOnly =
          this.mode === 'thread' && !userIds.includes(this.authUserId);

        if (this.viewOnly) {
          this.selectUserControl.disable();
        } else {
          this.selectUserControl.enable();
        }
      }
    );
    this.subscriptions.add(userIdsSubscribe);
  }

  private updateFilteredUsers(value: string) {
    const spaceUserIds = this.space
      ? this.authUserService
          .getSpaceUserData({ orgId: this.orgId, spaceId: this.space._id })
          .map(({ _id }) => _id)
      : [];

    const matchingUsersAll = value
      ? this.authUserService.searchUsers({ orgId: this.org._id, value })
      : this.org.users;

    const matchingUsers = matchingUsersAll.filter((m) => !m.archivedAt);
    // Remove users that are already on the space and are archived
    const existingUserIds = this.users.map((u) => u._id);
    this.filteredUsers = matchingUsers.filter(
      (u) =>
        !existingUserIds.includes(u._id) &&
        (this.mode === 'thread' || spaceUserIds.includes(u._id))
    );
    this.existingFilteredUsers = matchingUsers.filter(
      (u) =>
        existingUserIds.includes(u._id) &&
        (this.mode === 'thread' || spaceUserIds.includes(u._id))
    );
    this.notInSpaceFilteredUsers = matchingUsers.filter(
      (u) =>
        !existingUserIds.includes(u._id) &&
        this.mode === 'task' &&
        !spaceUserIds.includes(u._id)
    );
  }

  addUser(userId: string) {
    // Reset text immediately
    this.selectUserControl.setValue('');
    this.threadItem.addUser({ userId });
  }

  leave() {
    const { orgId, refId, mode } = this;
    const dialogConfig = new MatDialogConfig();
    const data: ThreadLeaveDialogComponentData = {
      orgId,
      refId,
      mode,
      parentDialogRef: this.dialogRef,
    };
    dialogConfig.data = data;
    this.dialog.open(ThreadLeaveDialogComponent, dialogConfig);
  }

  closeDialog(): void {
    this.dialogRef.close('Edit users dialog closed');
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
